import { RequestStatus } from 'RequestStatusModule/types';
import { SetRequired } from 'type-fest';
import { reduxHelper } from 'src';
import {
  setRequestStatus,
  clearRequestStatus,
  clearAllRequestStatuses,
} from 'RequestStatusModule/actionCreators';

const defaultRequestStatus: Omit<RequestStatus, 'id'> = {
  /**
   * Set initial = true in the payload. Used for determining if initially loading/fetching something
   */
  isLoading: false,
  isRequesting: false,
  isSuccess: false,
  isAborted: false,
  error: undefined,
  abortController: undefined,
};

class RequestStatusManager {
  public getRequestStatuses() {
    return reduxHelper.getState().requestStatusReducer;
  }

  public getRequestStatus(id: RequestStatus['id']): RequestStatus | undefined {
    const requestStatuses = this.getRequestStatuses();

    return requestStatuses[id];
  }

  public setRequestStatus({ id, ...rest }: SetRequired<RequestStatus, 'id'>) {
    const dispatch = reduxHelper.getDispatch();
    dispatch(setRequestStatus({ id, ...rest }));
  }

  /* -------------------------------------------------------------------------- */

  public getRequestStatusAbortController({
    requestStatusId,
  }: {
    requestStatusId: RequestStatus['id'];
  }) {
    return this.getRequestStatus(requestStatusId)?.abortController;
  }

  public abortRequest({ id }: { id: RequestStatus['id'] }) {
    const requestStatus = this.getRequestStatus(id);
    const abortController = requestStatus?.abortController;

    if (abortController) {
      abortController.abort();
      this.setRequestStatus({ id, ...defaultRequestStatus, isAborted: true });
    } else if (requestStatus?.isRequesting || requestStatus?.isLoading) {
      // Only warn if request is actively in progress without an abort controller
      // This shouldn't happen in normal operation
      console.warn(`Request status exists for ${id} but no abort controller found`);
    }
    // If requestStatus is undefined or request already completed, no action needed
  }

  public removeRequestStatus({ id }: { id: RequestStatus['id'] }) {
    this.abortRequest({ id });
    const dispatch = reduxHelper.getDispatch();
    dispatch(clearRequestStatus({ id }));
  }
}

export const AppRequestStatusManager = new RequestStatusManager();
